/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect } from "react";
import {
    TextField,
    Box,
    Paper,
    Stack,
    Typography,
    Button,
    FormControl,
    Checkbox,
    ListItemText,
    FormControlLabel,
    Switch,
    Autocomplete,
    Dialog,
    DialogActions, DialogContent,
    DialogContentText, DialogTitle,
    Divider
} from "@mui/material";
import apiHeaders from "./apiHeaders"
import LinkButton from './LinkButton';
import { useParams, useLocation } from "react-router-dom";
import { setRilletUserId } from "./storageUtils";
import { handleSessionExpiredClose } from "./logout";

const isConnected = !!sessionStorage.getItem('rilletUserId');

function Integration() {
    const [error, setError] = useState("");
    const storedCompanyName = sessionStorage.getItem("companyName");
    const [companyName, setCompanyName] = useState(storedCompanyName || "");
    const params = useParams();
    const location = useLocation();

    useEffect(() => {
        if (params && params.rilletUserId && params.rilletUserId !== ":rilletUserId") {
            setRilletUserId(params.rilletUserId);
            window.dispatchEvent(new Event("RilletUserIdChange"));
        }
    }, [location.search, params.rilletUserId]);
    const handleTokenSubmit = async (event) => {

    };

    const [isConnected, setIsConnected] = useState(!!sessionStorage.getItem('rilletUserId'));
    useEffect(() => {
        setIsConnected(!!sessionStorage.getItem('rilletUserId'));
    }, []);

    const [buttonImage, setButtonImage] = useState("/connect-blue.svg");
    // Function to handle the image change on hover
    const handleMouseEnter = () => {
        setButtonImage("/connect-white.svg"); // Image on hover
    };

    const handleMouseLeave = () => {
        setButtonImage("/connect-blue.svg"); // Default image
    };

    const [disconnectImage, setDisconnectImage] = useState("/disconnect-blue.svg");
    const handleDisconnectMouseEnter = () => setDisconnectImage("/disconnect-white.svg");
    const handleDisconnectMouseLeave = () => setDisconnectImage("/disconnect-blue.svg");


    function cleanIntegrationUrl() {
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        if (pathSegments[0] === 'integration' && pathSegments.length > 1) {
            const newUrl = `${window.location.origin}/integration${window.location.search}`;
            window.history.replaceState({}, '', newUrl);
        }
    }

    const fetchCompanyName = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/company?tenantId=${sessionStorage.getItem('rilletUserId')}`, {
                headers: {
                    ...apiHeaders
                }
            });

            if (res.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setCompanyName(data.companyName);
                sessionStorage.setItem("companyName", data.companyName);
            }
        } catch (err) {
            console.error("Failed to fetch company name", err);
        }
    };

    const handleConnect = async () => {
        const isConnected = !!sessionStorage.getItem('rilletUserId');
        if (isConnected) {
            // Disconnect logic
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/revoke?tenantId=${sessionStorage.getItem('rilletUserId')}`,
                {
                    method: "GET",
                    headers: {
                        ...apiHeaders,
                        "Authorization": `${sessionStorage.getItem('token')}`
                    }
                }
            );

            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            sessionStorage.clear();
            setIsConnected(false);
            cleanIntegrationUrl();

        } else {
            // Connect logic
            // const res = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/auth/connect`, {
            //     method: "GET",
            //     headers: apiHeaders
            // });
            // const data = await res.json();
            // window.location.href = data.url;
        }
        window.dispatchEvent(new Event("RilletUserIdChange"));

    };
    useEffect(() => {
        const fetchtoken = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const sessionId = urlParams.get("s_id");

            if (!sessionStorage.getItem("companyName")) {
                fetchCompanyName();
            }

            if (sessionId) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/session?sessionId=${sessionId}`,
                        {
                            method: "GET",
                            headers: {
                                ...apiHeaders
                            }
                        }
                    );
                    if (response.ok) {
                        const result = await response.json();
                        sessionStorage.setItem("token", result.token);
                        if (!sessionStorage.getItem("companyName")) {
                            fetchCompanyName();
                        }
                    }
                    else {
                        const result = response.json();
                        setError(result.message);
                    }
                } catch (err) {
                    setError("Internal server error. Please try again.");
                }
                // Remove sessionId from URL without reloading the page
                urlParams.delete("s_id");
                const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
                window.history.replaceState({}, "", newUrl);
            }
        };
        fetchtoken();
    }, []);

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            <Box sx={{ alignItems: "center" }}>
                <Typography
                    className="zamp-page-title"
                    sx={{ mb: 2, textAlign: "center" }}
                >
                    Rillet with Zamp
                </Typography>
            </Box>

            <Box component="form" onSubmit={handleTokenSubmit}>
                <Paper elevation={6} sx={{ p: 4, maxWidth: 800, margin: "auto" }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        In order to use the Zamp Tax app for Rillet, you must connect your Rillet account. The Zamp Tax app's configurations will not be accessible until your Rillet account has been authenticated.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        You may choose to launch the Zamp Tax integrations menu, or connect via the button below.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        If you are already connected and would like to disconnect the Zamp Tax app from Rillet, please choose the "Disconnect" link below.
                    </Typography>
                    <Typography variant="body1">
                        If you need assistance, please contact{" "}
                        <a
                            href="mailto:support@zamp.com"
                            style={{ color: "#1976d2", textDecoration: "none" }}
                        >
                            support@zamp.com
                        </a>.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            mt: 3,
                            position: "relative",
                            minHeight: "60px",
                        }}
                    >
                        {/* Disconnect text+button on the left (if connected) */}
                        {isConnected && (
                            <Box sx={{ ml: 2, display: "flex", alignItems: "center", flexGrow: 1 }}>
                                <Typography variant="body1" sx={{ mr: 1 }}>
                                    You are connected to Rillet
                                    {companyName && (
                                        <Box component="span" sx={{ fontStyle: "italic" }}>
                                            {" "}({companyName})
                                        </Box>
                                    )}
                                </Typography>

                                <Button
                                    onClick={handleConnect}
                                    sx={{
                                        padding: 0,
                                        minWidth: "auto",
                                        background: "transparent",
                                        "&:hover img": {
                                            opacity: 0.8, // Example hover effect
                                            transform: "scale(1.05)",
                                        }
                                    }}
                                >
                                    <img
                                        src="/disconnect-blue.svg"
                                        alt="Disconnect from Rillet"
                                        style={{
                                            height: "40px",
                                            transition: "all 0.3s ease",
                                        }}
                                    />
                                </Button>
                            </Box>
                        )}


                        {/* Connect button centered (if not connected) */}
                        {!isConnected && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                }}
                            >
                                <Button
                                    onClick={handleConnect}
                                    sx={{
                                        padding: 0,
                                        minWidth: "auto",
                                        background: "transparent",
                                        "&:hover img": {
                                            opacity: 0.8, // You can also use transform, filter, etc.
                                        }
                                    }}
                                >
                                    <img
                                        src="/connect-blue.svg"
                                        alt="Connect to Rillet"
                                        style={{
                                            height: "40px",
                                            transition: "opacity 0.3s ease", // Smooth hover transition
                                        }}
                                    />
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );


}
export default Integration;
